import { useState, useRef, useEffect } from "react";
import { Clock, ArrowRight } from "lucide-react";

const categoryMedia = {
  "Laser Therapie Frauen": {
    video: "/laser-frauen.mp4",
    endImage: null,
  },
  "Laser Therapie Männer": {
    video: "/laser-maenner.mp4",
    endImage: "/laser-maenner-end.jpeg",
  },
  "Gesichtsbehandlungen": {
    video: "/futura-behandlung.mp4",
    endImage: null,
  },
  "Empfohlen": {
    video: "/futura-behandlung.mp4",
    endImage: null,
  },
};

const FeaturedMedia = ({ activeCategory, fallbackImage }) => {
  const media = categoryMedia[activeCategory];
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const [showEnd, setShowEnd] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    setShowEnd(false);
    setVideoError(false);
    clearTimeout(timerRef.current);
    const v = videoRef.current;
    if (v) {
      if (isFinite(v.duration)) v.currentTime = 0;
      v.play().catch(() => {});
    }
    return () => clearTimeout(timerRef.current);
  }, [activeCategory]);

  if (!media || videoError) {
    return (
      <img
        src={fallbackImage}
        alt={activeCategory}
        className="w-full h-full min-h-[300px] md:min-h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
      />
    );
  }

  const handleEnded = () => {
    if (!media.endImage) return;
    setShowEnd(true);
    timerRef.current = setTimeout(() => {
      setShowEnd(false);
      const v = videoRef.current;
      if (v) {
        v.currentTime = 0;
        v.play().catch(() => {});
      }
    }, 2500);
  };

  return (
    <>
      <video
        ref={videoRef}
        src={media.video}
        autoPlay
        muted
        playsInline
        preload="metadata"
        poster={fallbackImage}
        loop={!media.endImage}
        onEnded={handleEnded}
        onError={() => setVideoError(true)}
        style={{ filter: "brightness(1.18) contrast(1.03) saturate(1.05)" }}
        data-testid={`featured-video-${activeCategory.replace(/\s+/g, "-").toLowerCase()}`}
        className="w-full h-full min-h-[300px] md:min-h-[400px] object-cover"
      />
      {media.endImage && (
        <img
          src={media.endImage}
          alt={`${activeCategory} – Ergebnis`}
          data-testid="featured-end-image"
          className={`absolute inset-0 w-full h-full min-h-[300px] md:min-h-[400px] object-cover transition-opacity duration-500 ${
            showEnd ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </>
  );
};

export const ServicesSection = ({ services, onBookService }) => {
  const categories = [...new Set(services.map((s) => s.category))];
  const [activeCategory, setActiveCategory] = useState(categories[0] || "");

  const filteredServices = services.filter(
    (s) => s.category === activeCategory
  );

  const categoryImages = {
    "Empfohlen": "https://images.unsplash.com/photo-1713085085470-fba013d67e65?q=80&w=2070&auto=format&fit=crop",
    "Laser Therapie Frauen": "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?q=80&w=2070&auto=format&fit=crop",
    "Lasertherapie": "https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?q=80&w=2070&auto=format&fit=crop",
    "Laser Therapie Männer": "https://images.unsplash.com/photo-1519824145371-296894a0daa9?q=80&w=2070&auto=format&fit=crop",
    "Gesichtsbehandlungen": "https://images.unsplash.com/photo-1731514771613-991a02407132?q=80&w=2070&auto=format&fit=crop",
    "Brows & Lashes": "https://images.unsplash.com/photo-1516220362602-dba5272034e7?q=80&w=1969&auto=format&fit=crop",
    "Wax": "https://images.unsplash.com/photo-1587179790059-5f5d937fb87d?q=80&w=2071&auto=format&fit=crop",
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-[#C5A059] text-sm tracking-[0.3em] uppercase mb-4"
            data-testid="services-tagline"
          >
            Unsere Behandlungen
          </p>
          <h2
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight mb-6"
            data-testid="services-title"
          >
            Luxuriöse <span className="italic">Pflege</span>
          </h2>
          <div className="w-16 h-[1px] bg-gradient-to-r from-[#C5A059] to-[#3d7d83] mx-auto" />
        </div>

        {/* Category Tabs */}
        <div
          className="flex flex-wrap justify-center gap-3 md:gap-6 mb-12"
          data-testid="services-category-tabs"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              data-testid={`category-tab-${category.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm tracking-wider uppercase transition-all duration-300 ${
                activeCategory === category
                  ? "bg-[#1A1A1A] text-white"
                  : "bg-transparent text-[#666666] hover:text-[#1A1A1A] border border-[#E5E5E5] hover:border-[#1A1A1A]"
              } rounded-full`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Featured Image Card */}
          <div className="md:col-span-2 lg:col-span-1 lg:row-span-2 relative overflow-hidden group bg-[#1A1A1A]"
            data-testid="services-featured-image"
          >
            <FeaturedMedia
              activeCategory={activeCategory}
              fallbackImage={categoryImages[activeCategory] || categoryImages["Gesichtsbehandlungen"]}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent pointer-events-none" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-white/80 text-sm tracking-wider uppercase mb-2">
                Kategorie
              </p>
              <h3 className="font-playfair text-2xl md:text-3xl text-white">
                {activeCategory}
              </h3>
            </div>
          </div>

          {/* Service Cards */}
          {filteredServices.map((service, index) => (
            <div
              key={service.id}
              className="service-card bg-[#F9F8F6] p-6 md:p-8 border border-transparent group cursor-pointer"
              onClick={() => onBookService(service)}
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <h3
                  className="font-playfair text-xl md:text-2xl text-[#1A1A1A] group-hover:text-[#C5A059] transition-colors duration-300"
                  data-testid={`service-name-${service.id}`}
                >
                  {service.name}
                </h3>
                <ArrowRight
                  size={20}
                  className="text-[#C5A059] opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                />
              </div>

              {service.description && (
                <p
                  className="text-[#666666] text-sm mb-6 leading-relaxed"
                  data-testid={`service-desc-${service.id}`}
                >
                  {service.description}
                </p>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-[#E5E5E5]">
                <div className="flex items-center gap-2 text-[#666666]">
                  <Clock size={16} className="text-[#3d7d83]" />
                  <span className="text-sm" data-testid={`service-duration-${service.id}`}>
                    {service.duration}
                  </span>
                </div>
                <span
                  className="font-playfair text-xl text-[#1A1A1A]"
                  data-testid={`service-price-${service.id}`}
                >
                  {service.price}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => onBookService(null)}
            data-testid="services-cta-btn"
            className="btn-primary"
          >
            Alle Behandlungen ansehen
          </button>
        </div>
      </div>
    </section>
  );
};
